import {
  EditorAnimationFrame,
  EditorAnimationParams,
} from './editor-domain.models';

/**
 * Pure transforms over animation frame lists. The first frame is the fixed
 * source frame: it can never be moved, duplicated ahead of or deleted.
 * All functions return the input params unchanged when the operation is a no-op.
 */

/**
 * "pic.png" → "pic copy.png", duplicating a copy yields "pic copy 2.png" etc. —
 * names stay short no matter how many times a frame is duplicated.
 */
export function buildCopyName(name: string, existingNames: readonly string[]): string {
  const dotIndex = name.lastIndexOf('.');
  const hasExtension = dotIndex > 0;
  const stem = hasExtension ? name.slice(0, dotIndex) : name;
  const extension = hasExtension ? name.slice(dotIndex) : '';

  const base = stem.replace(/ copy(?: \d+)?$/, '');
  const taken = new Set(existingNames);

  let candidate = `${base} copy${extension}`;
  for (let index = 2; taken.has(candidate); index++) {
    candidate = `${base} copy ${index}${extension}`;
  }

  return candidate;
}

export function appendAnimationFrames(
  params: EditorAnimationParams,
  frames: readonly Omit<EditorAnimationFrame, 'id'>[],
  createFrameId: () => string,
): EditorAnimationParams {
  return {
    ...params,
    frames: [
      ...params.frames,
      ...frames.map(frame => ({
        ...frame,
        id: createFrameId(),
      })),
    ],
  };
}

export function setAnimationFrameDuration(
  params: EditorAnimationParams,
  id: string,
  durationMs: number,
): EditorAnimationParams {
  return {
    ...params,
    frames: params.frames.map(frame =>
      frame.id === id
        ? {
            ...frame,
            durationMs: Math.max(1, Math.round(durationMs || 1)),
          }
        : frame,
    ),
  };
}

export function moveAnimationFrame(
  params: EditorAnimationParams,
  id: string,
  position: number,
): EditorAnimationParams {
  const frames = [...params.frames];
  const previousIndex = frames.findIndex(frame => frame.id === id);

  if (previousIndex <= 0) {
    return params;
  }

  const [frame] = frames.splice(previousIndex, 1);
  frames.splice(Math.max(1, Math.min(position, frames.length)), 0, frame);

  return {
    ...params,
    frames,
  };
}

export function duplicateAnimationFrame(
  params: EditorAnimationParams,
  id: string,
  createFrameId: () => string,
): EditorAnimationParams {
  const frameIndex = params.frames.findIndex(frame => frame.id === id);

  if (frameIndex <= 0) {
    return params;
  }

  const frame = params.frames[frameIndex];
  const frames = [...params.frames];
  frames.splice(frameIndex + 1, 0, {
    ...frame,
    id: createFrameId(),
    name: buildCopyName(frame.name, params.frames.map(existing => existing.name)),
  });

  return {
    ...params,
    frames,
  };
}

export function deleteAnimationFrame(
  params: EditorAnimationParams,
  id: string,
): EditorAnimationParams {
  const frames = params.frames.filter((frame, index) => index === 0 || frame.id !== id);

  if (frames.length === params.frames.length) {
    return params;
  }

  return {
    ...params,
    frames,
  };
}
